import * as React from "react";
import { MenuItem, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";

import { useOpenAIAccounts } from "@/api/hooks";
import type { ServiceTier } from "@/api/types";

/** Service-tier picker with OAuth-aware gating.
 *
 *  Codex's ChatGPT-OAuth transport silently strips ``service_tier``
 *  from outbound payloads, and injecting it explicitly triggers
 *  OpenAI's ``Unsupported service_tier`` 400 (see openai/codex#14204).
 *  Only API-key auth honours the parameter. When the session's
 *  bound OpenAI account is OAuth, this picker locks to the standard
 *  tier and tells the user why — without this, picking flex/fast
 *  here would silently produce no behaviour change *or* fail the
 *  next turn outright.
 *
 *  ``openaiAccountId`` may be null (a session without a credential
 *  picked yet); in that case we keep the picker enabled so the form
 *  doesn't lock up — the same picker is reused in the new-session
 *  flow where the account is sometimes only chosen later. The
 *  backend gate (``_resolve_safe_service_tier``) is the safety net
 *  for any combination this UI can't see.
 */
export function ServiceTierField({
  value,
  onChange,
  openaiAccountId,
  locked = false,
}: {
  value: "" | ServiceTier;
  onChange: (next: "" | ServiceTier) => void;
  openaiAccountId: string | null | undefined;
  /** Fixed by the session's codex thread (``codex_settings_locked``). */
  locked?: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();
  const accounts = useOpenAIAccounts();
  const boundAccount = React.useMemo(() => {
    if (!openaiAccountId) return undefined;
    return (accounts.data ?? []).find((a) => a.id === openaiAccountId);
  }, [accounts.data, openaiAccountId]);
  const isOauth = boundAccount?.kind === "oauth";

  // Force the visible value to standard on OAuth, even if the
  // session row still carries a stale flex/fast pick. The backend
  // also coerces, but we surface the effective state here so the
  // UI doesn't lie about what'll happen on the next turn.
  // A locked tier is the one the thread runs with, whatever the account now.
  const effectiveValue = isOauth && !locked ? "" : value;

  const helperText = locked
    ? t(
        "session.codex_settings_locked_hint",
        "Fixed for this session after the first message; start a new session to change it.",
      )
    : isOauth
    ? t(
        "session.service_tier_hint_oauth",
        "Locked to the standard tier — flex / fast (priority) require an API-key OpenAI credential. This session is bound to a ChatGPT-OAuth account, which doesn't accept the parameter (openai/codex#14204).",
      )
    : t(
        "session.service_tier_hint",
        "Flex: ~50% cheaper, slower — supported only on gpt-5.4 / gpt-5.5 family (NOT on gpt-*-codex models). Fast: priority routing, ~2× cost. Blank = OpenAI's standard tier (works on every model).",
      );

  return (
    <TextField
      size="small"
      select
      label={t("session.service_tier_label", "Service tier")}
      value={effectiveValue}
      disabled={isOauth || locked}
      onChange={(e) => onChange(e.target.value as "" | ServiceTier)}
      helperText={helperText}
    >
      <MenuItem value="">
        {t("session.service_tier_default", "(standard)")}
      </MenuItem>
      <MenuItem value="flex" disabled={isOauth}>
        {t("session.service_tier_flex", "Flex (cheaper)")}
      </MenuItem>
      <MenuItem value="fast" disabled={isOauth}>
        {t("session.service_tier_fast", "Fast (priority)")}
      </MenuItem>
    </TextField>
  );
}
